import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import AaSequenceSearchInput from './AaSequenceSearchInput';
import {AaSequenceSpecifier} from './util/AaSequenceAlignmentConstants';
import specifierForAaSequence from './util/specifierForAaSequence';

interface Props {
  proteinSpecifiers: Array<AaSequenceSpecifier>;
  templateProteinIndex?: number | null;
  onAddAaSequenceSpecifier: (aaSequenceSpecifier: AaSequenceSpecifier) => void;
  onRemoveAaSequenceAt: (rowIndex: number) => void;
  onSetTemplateAaSequenceAt: (rowIndex: number | null) => void;
  allowSetTemplate: boolean;
}

class ChooseAaSequenceAlignmentPane extends React.Component<Props> {
  static defaultProps = {
    allowSetTemplate: true,
  };

  renderAaSequenceRow = (proteinName: string, rowIndex: number): JSX.Element => {
    const allowDelete = this.props.allowSetTemplate || rowIndex !== this.props.templateProteinIndex;
    return (
      <tr className="template _test-proteinRow" key={`${proteinName}-${rowIndex}`}>
        {this.renderAaSequenceCell(proteinName, rowIndex === this.props.templateProteinIndex)}
        {this.props.allowSetTemplate ? this.renderTemplateCell(rowIndex) : <td />}
        {allowDelete ? this.renderDeleteCell(rowIndex) : <td />}
      </tr>
    );
  };

  renderAaSequenceCell = (proteinName: string, isTemplateAaSequence: boolean): JSX.Element => (
    <td {...getTestProps({component: 'ChooseProteinAlignmentPane', element: 'proteinCell'})}>
      <span className="chooseProteinAlignment-proteinName">{proteinName}</span>
      <br />
      {isTemplateAaSequence && (
        <span
          className="chooseProteinAlignment-proteinDescription"
          {...getTestProps({
            component: 'ChooseProteinAlignmentPane',
            element: 'proteinDescriptionTemplate',
          })}
        >
          Template
        </span>
      )}
    </td>
  );

  renderTemplateCell = (rowIndex: number): JSX.Element => {
    if (rowIndex === this.props.templateProteinIndex) {
      return (
        <td
          className="chooseProteinAlignment-templateCell"
          {...getTestProps({component: 'ChooseProteinAlignmentPane', element: 'templateCell'})}
        >
          <a
            className="chooseProteinAlignment-hoverButton"
            onClick={() => {
              this.props.onSetTemplateAaSequenceAt(null);
            }}
          >
            Unset Template
          </a>
        </td>
      );
    } else {
      return (
        <td
          className="chooseProteinAlignment-templateCell"
          {...getTestProps({component: 'ChooseProteinAlignmentPane', element: 'templateCell'})}
        >
          <a
            className="chooseProteinAlignment-hoverButton"
            onClick={() => {
              this.props.onSetTemplateAaSequenceAt(rowIndex);
            }}
          >
            Set Template
          </a>
        </td>
      );
    }
  };

  renderDeleteCell = (rowIndex: number): JSX.Element => (
    <td className="chooseProteinAlignment-deleteCell">
      <a
        className="faded u-danger-pointer chooseProteinAlignment-hoverButton"
        onClick={() => {
          this.props.onRemoveAaSequenceAt(rowIndex);
        }}
      >
        <BenchlingIcon icon="delete" />
      </a>
    </td>
  );

  renderAaSequenceTable = (): JSX.Element => {
    if (this.props.proteinSpecifiers.length === 0) {
      return (
        <div className="chooseProteinAlignment-noProteinsText">No AA sequences chosen for alignment.</div>
      );
    } else {
      return (
        <table className="table table-hover">
          <tbody>
            {this.props.proteinSpecifiers.map((aaSequenceSpecifier, rowIndex) =>
              this.renderAaSequenceRow(aaSequenceSpecifier.name, rowIndex)
            )}
          </tbody>
        </table>
      );
    }
  };

  override render(): JSX.Element {
    return (
      <div>
        <div className="u-tiny-spacing-bottom">Add AA sequences to your alignment:</div>
        <AaSequenceSearchInput
          onChooseAaSequence={(protein) => {
            this.props.onAddAaSequenceSpecifier(specifierForAaSequence(protein));
          }}
        />
        <h4 className="chooseProteinAlignment-tableTitle">AA Sequences</h4>
        {this.renderAaSequenceTable()}
      </div>
    );
  }
}

export default ChooseAaSequenceAlignmentPane;
